import { AdType } from '../types';

export const buildAdId = (
  type: string,
  modifier?: string,
  positionIncrement?: number
) => [type, modifier, positionIncrement].filter(Boolean).join('-');

const getPosition = (adType: AdType, positionIncrement?: number) =>
  `${adType}${positionIncrement || ''}`;

const getLeaderboardAdOptions = (positionIncrement?: number) => ({
  strategy: 'always',
  dimensions: {
    all: [[5, 16]],
    large_desktop: [[970, 250]],
    small_desktop: [[970, 250]],
    tablet: [
      [320, 50],
      [300, 50],
    ],
    mobile: [
      [320, 50],
      [300, 50],
    ],
  },
  targeting: {
    position: getPosition('leaderboard', positionIncrement),
  },
});

const getImmersiveAdOptions = (positionIncrement?: number) => ({
  dimensions: {
    all: [
      [5, 16],
      [5, 5],
    ],
    large_desktop: [[970, 250]],
    small_desktop: [[970, 250]],
    tablet: [
      [320, 50],
      [300, 50],
    ],
    mobile: [
      [300, 250],
      [3, 3],
      [5, 19],
    ],
  },
  targeting: {
    position: getPosition('immersive', positionIncrement),
  },
});

const getImmersiveDesktopAdOptions = (positionIncrement?: number) => ({
  dimensions: {
    large_desktop: [
      [970, 250],
      [5, 16],
      [5, 5],
    ],
    small_desktop: [
      [970, 250],
      [5, 16],
      [5, 5],
    ],
  },
  targeting: {
    position: getPosition('immersive', positionIncrement),
  },
});

const getImmersiveTabletAdOptions = (positionIncrement?: number) => ({
  dimensions: {
    tablet: [
      [5, 16],
      [5, 5],
      [320, 50],
      [300, 50],
    ],
  },
  targeting: {
    position: getPosition('immersive', positionIncrement),
  },
});

const getImmersiveMobileAdOptions = (positionIncrement?: number) => ({
  dimensions: {
    all: [
      [300, 250],
      [3, 3],
      [5, 16],
      [5, 5],
      [5, 19],
    ],
  },
  targeting: {
    position: getPosition('immersive', positionIncrement),
  },
});

const getInBodyAdOptions = (positionIncrement?: number) => ({
  dimensions: {
    all: [
      [300, 250],
      [3, 3],
      [5, 24],
    ],
    large_desktop: [],
    small_desktop: [],
    tablet: [[5, 11]],
    mobile: [[3, 4]],
  },
  targeting: {
    position: getPosition('in-body', positionIncrement),
  },
});

const getBoxAdOptions = (positionIncrement?: number) => ({
  dimensions: {
    all: [
      [300, 250],
      [3, 3],
    ],
    large_desktop: [],
    small_desktop: [],
    mobile: [
      [5, 16],
      [5, 5],
      [5, 19],
    ],
  },
  targeting: {
    position: getPosition('box', positionIncrement),
  },
});

const getBoxDesktopAdOptions = (positionIncrement?: number) => ({
  dimensions: {
    large_desktop: [
      [300, 250],
      [3, 3],
    ],
    small_desktop: [
      [300, 250],
      [3, 3],
    ],
  },
  targeting: {
    position: getPosition('box', positionIncrement),
  },
});

const getFlexBoxAdOptions = (positionIncrement?: number) => ({
  dimensions: {
    large_desktop: [
      [300, 600],
      [300, 250],
      [3, 3],
      [3, 4],
    ],
    small_desktop: [
      [300, 600],
      [300, 250],
      [3, 3],
      [3, 4],
    ],
  },
  targeting: {
    position: getPosition('box', positionIncrement),
  },
});

const getBrokerBoxOptions = (positionIncrement?: number, v2?: boolean) => ({
  dimensions: {
    all: v2 ? [[100, 48]] : [[120, 60]],
  },
  targeting: {
    position: getPosition('broker-box', positionIncrement),
  },
});

const getCombinedBrokerBoxOptions = (positionIncrement?: number) => ({
  dimensions: {
    all: [
      [120, 60],
      [240, 60],
    ],
  },
  targeting: {
    position: getPosition('combined-broker-box', positionIncrement),
  },
});

const getLineupSFHeaderSponConAdOptions = () => ({
  dimensions: {
    all: [
      [88, 31],
      [1, 1],
    ],
  },
  strategy: 'always',
  targeting: {
    position: getPosition('sponsoredby'),
  },
});

const getSponsoredByBrandAdOptions = () => ({
  dimensions: {
    all: [[290, 62]],
  },
  targeting: {
    position: getPosition('sponsoredby-brand'),
  },
});

const getQuizAdOptions = () => ({
  dimensions: {
    all: [[3, 5]],
  },
  targeting: {
    position: getPosition('quiz'),
  },
});

type AdOptionsType =
  | {
      strategy?: string;
      dimensions: {
        all: number[][];
        large_desktop: number[][];
        small_desktop: number[][];
        mobile: number[][];
      };
      targeting: {
        position: string;
      };
    }
  | {
      dimensions: {
        large_desktop: number[][];
        small_desktop: number[][];
      };
      targeting: {
        position: string;
      };
    }
  | {
      dimensions: {
        tablet: number[][];
      };
      targeting: {
        position: string;
      };
    }
  | {
      dimensions: {
        mobile: number[][];
      };
      targeting: {
        position: string;
      };
    }
  | {
      dimensions: {
        all: number[][];
      };
      targeting: {
        position: string;
      };
    }
  | Record<string, never>;

export const getAdOptions = (
  type: AdType,
  positionIncrement?: number
): AdOptionsType => {
  switch (type) {
    case 'leaderboard':
      return getLeaderboardAdOptions(positionIncrement);

    case 'in-body':
      return getInBodyAdOptions(positionIncrement);

    case 'box':
      return getBoxAdOptions(positionIncrement);

    case 'box-desktop':
      return getBoxDesktopAdOptions(positionIncrement);

    case 'flexbox':
      return getFlexBoxAdOptions(positionIncrement);

    case 'immersive':
      return getImmersiveAdOptions(positionIncrement);

    case 'immersive-desktop':
      return getImmersiveDesktopAdOptions(positionIncrement);

    case 'immersive-tablet':
      return getImmersiveTabletAdOptions(positionIncrement);

    case 'immersive-mobile':
      return getImmersiveMobileAdOptions(positionIncrement);

    case 'broker-box':
      return getBrokerBoxOptions(positionIncrement);

    case 'combined-broker-box':
      return getCombinedBrokerBoxOptions(positionIncrement);

    case 'broker-box-v2':
      return getBrokerBoxOptions(positionIncrement, true);

    case 'sponsoredby':
      return getLineupSFHeaderSponConAdOptions();

    case 'sponsoredby-brand':
      return getSponsoredByBrandAdOptions();

    case 'quiz':
      return getQuizAdOptions();
    default:
      return {};
  }
};
